import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Claim } from '@wsphere/warranties/domain';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class ClaimItemLineService {
  private http = inject(HttpClient);

  // The API caps limit at 100 and the lines table sums job totals client-side, so fetch every page.
  async list(claimId: string, itemId: string) {
    const limit = 100;
    const items: Claim.ClaimItem.Line.Model[] = [];

    let page = 1;
    let totalPages = 1;

    do {
      const response = await firstValueFrom(
        this.http.get<Claim.ClaimItem.Line.ListClaimItemLinesQueryResponse>(
          `/api/claims/${claimId}/items/${itemId}/lines`,
          { params: { page, limit } }
        )
      );

      items.push(...response.items);
      totalPages = response.meta?.totalPages ?? 1;
      page++;
    } while (page <= totalPages);

    return { items };
  }

  create(claimId: string, itemId: string, body: Claim.ClaimItem.Line.CreateClaimItemLineRequest) {
    return firstValueFrom(
      this.http.post<Claim.ClaimItem.Line.CreateClaimItemLineResponse>(
        `/api/claims/${claimId}/items/${itemId}/lines`,
        body
      )
    );
  }

  update(claimId: string, itemId: string, lineId: string, body: Claim.ClaimItem.Line.UpdateClaimItemLineRequest) {
    return firstValueFrom(
      this.http.patch<Claim.ClaimItem.Line.UpdateClaimItemLineResponse>(
        `/api/claims/${claimId}/items/${itemId}/lines/${lineId}`,
        body
      )
    );
  }

  units() {
    return firstValueFrom(this.http.get<Claim.ClaimItem.Line.RetrieveUnitsResponse>(`/api/claim-item-line-units`));
  }

  adjust(claimId: string, itemId: string, lineId: string, body: Claim.ClaimItem.Line.AdjustClaimItemLineRequest) {
    return firstValueFrom(
      this.http.patch<Claim.ClaimItem.Line.AdjustClaimItemLineResponse>(
        `/api/claims/${claimId}/items/${itemId}/lines/${lineId}/adjust`,
        body
      )
    );
  }

  adjustments(claimId: string, itemId: string, lineId: string) {
    return firstValueFrom(
      this.http.get<Claim.ClaimItem.Line.ListAdjustmentsResponse>(
        `/api/claims/${claimId}/items/${itemId}/lines/${lineId}/adjustments`
      )
    );
  }

  remove(claimId: string, itemId: string, lineId: string, deletedReason?: string) {
    return firstValueFrom(
      this.http.delete<{ id: string }>(`/api/claims/${claimId}/items/${itemId}/lines/${lineId}`, {
        body: { ...(deletedReason && { deletedReason }) },
      })
    );
  }
}
